/**
 * AdminLicencasConsolidatedPage — Consolidated Licenses + Billing
 */
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronDown, ShieldCheck, CreditCard, Receipt } from 'lucide-react';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { AdminLicensesPage } from './AdminLicensesPage';
import { AdminBillingPage } from './AdminBillingPage';
import { desktopRibbonInputClass } from '../../../components/ribbon/DesktopRibbonControls';
import type { RibbonAction } from '../../../types/ribbon';

export const AdminLicencasConsolidatedPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'licencas';
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [billingView, setBillingView] = React.useState<'subscriptions' | 'invoices'>('subscriptions');

  // Reset filters when tab changes
  React.useEffect(() => {
    setSearch('');
    setStatusFilter('all');
    setBillingView('subscriptions');
  }, [activeTab]);

  const filterBtnClass = 'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const contextActions = useMemo<RibbonAction[]>(() => {
    if (activeTab !== 'billing') return [];
    return [
      { id: 'billing-subs', label: 'Assinaturas', icon: CreditCard, onClick: () => setBillingView('subscriptions'), active: billingView === 'subscriptions' },
      { id: 'billing-invoices', label: 'Faturas', icon: Receipt, onClick: () => setBillingView('invoices'), active: billingView === 'invoices' },
    ];
  }, [activeTab, billingView]);

  const filtersWidget = useMemo(() => {
    if (activeTab !== 'licencas') return null;
    return (
      <div className="flex flex-1 items-center justify-end gap-1.5">
        <div className="relative">
          <select
            className={`${filterBtnClass} appearance-none cursor-pointer`}
            style={{ paddingLeft: 22, paddingRight: 22 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos status</option>
            <option value="active">Ativo</option>
            <option value="trial">Trial</option>
            <option value="suspended">Suspenso</option>
            <option value="expired">Expirado</option>
          </select>
          <ShieldCheck size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
        </div>
        <div className="flex w-[220px] shrink-0 items-center">
          <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
            <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
            <input
              type="text"
              placeholder="Buscar organização..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
            />
            {search && (
              <button onClick={() => setSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
                <X size={10} className="text-[var(--text-secondary)]" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }, [activeTab, statusFilter, search]);

  return (
    <AdminConsoleLayout ribbonSlots={{ afterGroups: filtersWidget }} contextActions={contextActions}>
      <div className="h-full overflow-auto">
        {activeTab === 'licencas' && <AdminLicensesPage externalSearch={search} externalStatusFilter={statusFilter} />}
        {activeTab === 'billing' && <AdminBillingPage externalView={billingView} />}
      </div>
    </AdminConsoleLayout>
  );
};
